import { FinancialPlanData, GrowthTimelineResult, TimelineMilestone } from '../types/financialPlan';
import { incomePhaseLabelAtAge } from './financialPlanDefaults';
import {
  accountMonthlyContributionsAtAge,
  computeInvestablePoolAtAge,
} from './lgcAccountAllocation';
import {
  buildRequiredIncomeData,
  calendarYearFromPlanTimeline,
  RequiredIncomePoint,
} from './lgcRequiredIncomeSeries';
import {
  getSavingsBundleAtAge,
  livingMonthlyAtPlanAge,
  phasedSurplusTakeHomeAnnual,
  remainingRetirementGapAtAge,
  computeRequiredIncome,
} from './lifetimeGoalsCalc';
import { onPaceExtraMonthlyAtAge } from './lifetimeGoalsTimeline';

export type PlanDebugColumnGroup =
  | 'meta'
  | 'income'
  | 'spend'
  | 'savings'
  | 'affordability'
  | 'accounts_balances'
  | 'accounts_flows'
  | 'required_income'
  | 'checks';

export type PlanDebugColumnFormat = 'number' | 'currency' | 'currencyMo' | 'text' | 'pct';

export type PlanDebugDollarMode = 'today' | 'nominal' | 'both';

export type PlanYearDebugColumn = {
  key: keyof PlanYearDebugRow;
  label: string;
  group: PlanDebugColumnGroup;
  format: PlanDebugColumnFormat;
  /** When set, column is shown only for this dollar mode (or both if mode is 'both'). */
  dollarMode?: 'today' | 'nominal';
  /** Hidden by default until Checks group is enabled. */
  defaultHidden?: boolean;
};

export type PlanYearDebugRow = {
  age: number;
  calendarYear: number;
  phase: string;
  incomePhaseLabel: string;
  milestones: string;

  grossIncomeToday: number;
  grossIncomeNominal: number;
  takeHomeAnnual: number;
  takeHomeToday: number;
  requiredGrossToday: number;
  requiredGrossNominal: number;
  incomeGapToday: number;

  livingAnnual: number;
  livingToday: number;
  debtServiceAnnual: number;
  debtServiceToday: number;
  k12CashAnnual: number;
  k12CashToday: number;
  requiredSpendAnnual: number;
  requiredSpendToday: number;
  annualWithdrawal: number;

  saveRetirementMo: number;
  saveCollegeMo: number;
  saveK12Mo: number;
  saveHealthMo: number;
  saveLifestyleMo: number;
  requiredSaveMonthly: number;
  requiredSaveAnnual: number;
  requiredSaveToday: number;

  surplusAnnual: number;
  surplusToday: number;
  availableForSavings: number;
  availableForSavingsToday: number;
  phasedSurplusTakeHomeAnnual: number;
  phasedSurplusToday: number;
  retirementGapRemaining: number;

  configuredInvestMo: number;
  requiredRetMo: number;
  onPaceExtraMo: number;

  k401: number;
  rothIra: number;
  hsa: number;
  taxable: number;
  totalCurrent: number;
  onTrackK401: number;
  onTrackRothIra: number;
  onTrackHsa: number;
  onTrackTaxable: number;
  totalOnTrack: number;

  sepIraMonthly: number;
  contribK401: number;
  contribRoth: number;
  contribHsa: number;
  contribTaxable: number;
  poolAvailableMo: number;
  poolAllocatedMo: number;
  poolUnallocatedMo: number;
  primaryCommitmentsMo: number;

  stackBudgetToday: number;
  stackBudgetNominal: number;
  stackDebtToday: number;
  stackDebtNominal: number;
  stackK12Today: number;
  stackK12Nominal: number;
  stackSaveToday: number;
  stackSaveNominal: number;
  stackTaxToday: number;
  stackTaxNominal: number;
  stackIncomeToday: number;
  stackIncomeNominal: number;
  stackGrossRequiredToday: number;
  stackGrossRequiredNominal: number;

  checkSurplus: number;
  checkRequiredStack: number;
  checkPool: number;
};

const CHECK_TOLERANCE = 1;

function nominalToToday(timeline: GrowthTimelineResult, age: number, nominal: number): number {
  const years = age - timeline.currentAge;
  if (years <= 0 || timeline.inflationPct <= 0) return nominal;
  return nominal / Math.pow(1 + timeline.inflationPct / 100, years);
}

function milestoneLabelsAtAge(milestones: TimelineMilestone[], age: number): string {
  const labels = milestones.filter((m) => m.age === age).map((m) => m.label);
  return labels.join('; ');
}

function stackFields(stack: RequiredIncomePoint | undefined): Pick<
  PlanYearDebugRow,
  | 'stackBudgetToday'
  | 'stackBudgetNominal'
  | 'stackDebtToday'
  | 'stackDebtNominal'
  | 'stackK12Today'
  | 'stackK12Nominal'
  | 'stackSaveToday'
  | 'stackSaveNominal'
  | 'stackTaxToday'
  | 'stackTaxNominal'
  | 'stackIncomeToday'
  | 'stackIncomeNominal'
  | 'stackGrossRequiredToday'
  | 'stackGrossRequiredNominal'
> {
  if (!stack) {
    return {
      stackBudgetToday: 0,
      stackBudgetNominal: 0,
      stackDebtToday: 0,
      stackDebtNominal: 0,
      stackK12Today: 0,
      stackK12Nominal: 0,
      stackSaveToday: 0,
      stackSaveNominal: 0,
      stackTaxToday: 0,
      stackTaxNominal: 0,
      stackIncomeToday: 0,
      stackIncomeNominal: 0,
      stackGrossRequiredToday: 0,
      stackGrossRequiredNominal: 0,
    };
  }
  return {
    stackBudgetToday: stack.budget,
    stackBudgetNominal: stack.budgetNominal,
    stackDebtToday: stack.debt,
    stackDebtNominal: stack.debtNominal,
    stackK12Today: stack.k12,
    stackK12Nominal: stack.k12Nominal,
    stackSaveToday: stack.save,
    stackSaveNominal: stack.saveNominal,
    stackTaxToday: stack.tax,
    stackTaxNominal: stack.taxNominal,
    stackIncomeToday: stack.income,
    stackIncomeNominal: stack.incomeNominal,
    stackGrossRequiredToday: stack.grossRequired,
    stackGrossRequiredNominal: stack.grossRequiredNominal,
  };
}

export function buildPlanYearDebugRows(
  plan: FinancialPlanData,
  timeline: GrowthTimelineResult,
): PlanYearDebugRow[] {
  const requiredIncomeByAge = new Map(
    buildRequiredIncomeData(timeline).map((row) => [row.age, row]),
  );

  const globalRetCatchUp =
    computeRequiredIncome(plan).breakdown.find((b) => b.id === 'retirement')?.monthlyAmount ?? 0;

  return timeline.points.map((p) => {
    const age = p.age;
    const stack = requiredIncomeByAge.get(age);
    const savings = age < plan.profile.retirementAge ? getSavingsBundleAtAge(plan, age) : {
      retirement: 0,
      college: 0,
      k12: 0,
      health: 0,
      lifestyle: 0,
    };

    const livingAnnual = livingMonthlyAtPlanAge(plan, age) * 12;
    const livingToday = nominalToToday(timeline, age, livingAnnual);

    const grossIncomeToday = p.grossIncomeToday ?? 0;
    const grossIncomeNominal = p.grossIncome ?? grossIncomeToday;
    const requiredGrossNominal = p.requiredGrossAnnual ?? 0;
    const requiredGrossToday = stack?.grossRequired ?? nominalToToday(timeline, age, requiredGrossNominal);

    const takeHomeAnnual = p.takeHomeAnnual ?? 0;
    const takeHomeToday = nominalToToday(timeline, age, takeHomeAnnual);

    const debtServiceAnnual = p.debtServiceAnnual ?? 0;
    const k12CashAnnual = p.k12SpendingAnnual ?? 0;
    const requiredSpendAnnual = p.requiredSpendAnnual ?? 0;
    const requiredSaveAnnual = p.requiredSaveAnnual ?? 0;
    const requiredSaveMonthly = p.requiredSaveMonthly ?? 0;
    const surplusAnnual = p.surplusAnnual ?? 0;

    const pool =
      p.phase === 'accumulation' ? computeInvestablePoolAtAge(plan, age) : null;
    const contribs =
      p.phase === 'accumulation' ? accountMonthlyContributionsAtAge(plan, age) : null;
    const configuredInvestMo = contribs
      ? contribs.k401 + contribs.rothIra + contribs.hsa + contribs.taxable
      : 0;
    const requiredRetMo =
      p.phase === 'accumulation' && age < plan.profile.retirementAge
        ? getSavingsBundleAtAge(plan, age).retirement
        : 0;
    const onPaceExtraMo =
      p.phase === 'accumulation' && age < plan.profile.retirementAge
        ? onPaceExtraMonthlyAtAge(plan, age, globalRetCatchUp)
        : 0;

    const retirementGapRemaining =
      p.phase === 'accumulation' && age < plan.profile.retirementAge
        ? remainingRetirementGapAtAge(plan, {
            k401: p.onTrackK401,
            rothIra: p.onTrackRothIra,
            hsa: p.onTrackHsa,
            taxable: p.onTrackTaxable,
          }, age)
        : 0;

    const phasedSurplus = p.phase === 'accumulation' ? phasedSurplusTakeHomeAnnual(plan, age) : 0;

    const checkSurplus =
      takeHomeAnnual - requiredSpendAnnual - requiredSaveAnnual - surplusAnnual;
    const checkRequiredStack = stack
      ? stack.budgetNominal +
        stack.debtNominal +
        stack.k12Nominal +
        stack.saveNominal +
        stack.taxNominal -
        stack.grossRequiredNominal
      : 0;
    const poolBeforeClamp =
      pool != null
        ? pool.takeHomeMonthly - pool.primaryCommitmentsMonthly - pool.sepIraMonthly
        : 0;
    const checkPool =
      pool != null ? Math.max(0, poolBeforeClamp) - pool.availableToInvestMonthly : 0;

    return {
      age,
      calendarYear: calendarYearFromPlanTimeline(timeline, age),
      phase: p.phase,
      incomePhaseLabel: incomePhaseLabelAtAge(plan.income, age, plan.profile.retirementAge),
      milestones: milestoneLabelsAtAge(timeline.milestones, age),

      grossIncomeToday,
      grossIncomeNominal,
      takeHomeAnnual,
      takeHomeToday,
      requiredGrossToday,
      requiredGrossNominal,
      incomeGapToday: grossIncomeToday - requiredGrossToday,

      livingAnnual,
      livingToday,
      debtServiceAnnual,
      debtServiceToday: nominalToToday(timeline, age, debtServiceAnnual),
      k12CashAnnual,
      k12CashToday: nominalToToday(timeline, age, k12CashAnnual),
      requiredSpendAnnual,
      requiredSpendToday: nominalToToday(timeline, age, requiredSpendAnnual),
      annualWithdrawal: p.annualWithdrawal ?? 0,

      saveRetirementMo: savings.retirement,
      saveCollegeMo: savings.college,
      saveK12Mo: savings.k12,
      saveHealthMo: savings.health,
      saveLifestyleMo: savings.lifestyle,
      requiredSaveMonthly,
      requiredSaveAnnual,
      requiredSaveToday: nominalToToday(timeline, age, requiredSaveAnnual),

      surplusAnnual,
      surplusToday: nominalToToday(timeline, age, surplusAnnual),
      availableForSavings: p.availableForSavings ?? 0,
      availableForSavingsToday: nominalToToday(timeline, age, p.availableForSavings ?? 0),
      phasedSurplusTakeHomeAnnual: phasedSurplus,
      phasedSurplusToday: nominalToToday(timeline, age, phasedSurplus),
      retirementGapRemaining,

      configuredInvestMo,
      requiredRetMo,
      onPaceExtraMo,

      k401: p.k401,
      rothIra: p.rothIra,
      hsa: p.hsa,
      taxable: p.taxable,
      totalCurrent: p.totalCurrent,
      onTrackK401: p.onTrackK401,
      onTrackRothIra: p.onTrackRothIra,
      onTrackHsa: p.onTrackHsa,
      onTrackTaxable: p.onTrackTaxable,
      totalOnTrack: p.totalOnTrack,

      sepIraMonthly: pool?.sepIraMonthly ?? 0,
      contribK401: contribs?.k401 ?? 0,
      contribRoth: contribs?.rothIra ?? 0,
      contribHsa: contribs?.hsa ?? 0,
      contribTaxable: contribs?.taxable ?? 0,
      poolAvailableMo: pool?.availableToInvestMonthly ?? 0,
      poolAllocatedMo: pool?.allocatedMonthly ?? 0,
      poolUnallocatedMo: pool?.unallocatedMonthly ?? 0,
      primaryCommitmentsMo: pool?.primaryCommitmentsMonthly ?? 0,

      ...stackFields(stack),

      checkSurplus,
      checkRequiredStack,
      checkPool,
    };
  });
}

export const PLAN_DEBUG_COLUMN_GROUPS: { id: PlanDebugColumnGroup; label: string }[] = [
  { id: 'meta', label: 'Meta' },
  { id: 'income', label: 'Income' },
  { id: 'spend', label: 'Spend' },
  { id: 'savings', label: 'Savings need' },
  { id: 'affordability', label: 'Affordability' },
  { id: 'accounts_balances', label: 'Account balances' },
  { id: 'accounts_flows', label: 'Account flows' },
  { id: 'required_income', label: 'Required-income stack' },
  { id: 'checks', label: 'Consistency checks' },
];

export const PLAN_DEBUG_COLUMNS: PlanYearDebugColumn[] = [
  { key: 'calendarYear', label: 'Year', group: 'meta', format: 'number' },
  { key: 'phase', label: 'Phase', group: 'meta', format: 'text' },
  { key: 'incomePhaseLabel', label: 'Income phase', group: 'meta', format: 'text' },
  { key: 'milestones', label: 'Milestones', group: 'meta', format: 'text' },

  { key: 'grossIncomeToday', label: 'Gross income', group: 'income', format: 'currency', dollarMode: 'today' },
  { key: 'grossIncomeNominal', label: 'Gross income', group: 'income', format: 'currency', dollarMode: 'nominal' },
  { key: 'takeHomeToday', label: 'Take-home', group: 'income', format: 'currency', dollarMode: 'today' },
  { key: 'takeHomeAnnual', label: 'Take-home', group: 'income', format: 'currency', dollarMode: 'nominal' },
  { key: 'requiredGrossToday', label: 'Required gross', group: 'income', format: 'currency', dollarMode: 'today' },
  { key: 'requiredGrossNominal', label: 'Required gross', group: 'income', format: 'currency', dollarMode: 'nominal' },
  { key: 'incomeGapToday', label: 'Income gap', group: 'income', format: 'currency', dollarMode: 'today' },

  { key: 'livingToday', label: 'Living', group: 'spend', format: 'currency', dollarMode: 'today' },
  { key: 'livingAnnual', label: 'Living', group: 'spend', format: 'currency', dollarMode: 'nominal' },
  { key: 'debtServiceToday', label: 'Debt service', group: 'spend', format: 'currency', dollarMode: 'today' },
  { key: 'debtServiceAnnual', label: 'Debt service', group: 'spend', format: 'currency', dollarMode: 'nominal' },
  { key: 'k12CashToday', label: 'K-12 cash', group: 'spend', format: 'currency', dollarMode: 'today' },
  { key: 'k12CashAnnual', label: 'K-12 cash', group: 'spend', format: 'currency', dollarMode: 'nominal' },
  { key: 'requiredSpendToday', label: 'Required spend', group: 'spend', format: 'currency', dollarMode: 'today' },
  { key: 'requiredSpendAnnual', label: 'Required spend', group: 'spend', format: 'currency', dollarMode: 'nominal' },
  { key: 'annualWithdrawal', label: 'Withdrawal', group: 'spend', format: 'currency', dollarMode: 'nominal' },

  { key: 'saveRetirementMo', label: 'Save · retirement/mo', group: 'savings', format: 'currencyMo' },
  { key: 'saveCollegeMo', label: 'Save · college/mo', group: 'savings', format: 'currencyMo' },
  { key: 'saveK12Mo', label: 'Save · K-12/mo', group: 'savings', format: 'currencyMo' },
  { key: 'saveHealthMo', label: 'Save · health/mo', group: 'savings', format: 'currencyMo' },
  { key: 'saveLifestyleMo', label: 'Save · lifestyle/mo', group: 'savings', format: 'currencyMo' },
  { key: 'requiredSaveMonthly', label: 'Required save/mo', group: 'savings', format: 'currencyMo' },
  { key: 'requiredSaveToday', label: 'Required save/yr', group: 'savings', format: 'currency', dollarMode: 'today' },
  { key: 'requiredSaveAnnual', label: 'Required save/yr', group: 'savings', format: 'currency', dollarMode: 'nominal' },

  { key: 'surplusToday', label: 'Surplus', group: 'affordability', format: 'currency', dollarMode: 'today' },
  { key: 'surplusAnnual', label: 'Surplus', group: 'affordability', format: 'currency', dollarMode: 'nominal' },
  { key: 'availableForSavingsToday', label: 'Available for savings', group: 'affordability', format: 'currency', dollarMode: 'today' },
  { key: 'availableForSavings', label: 'Available for savings', group: 'affordability', format: 'currency', dollarMode: 'nominal' },
  { key: 'phasedSurplusToday', label: 'Phased surplus', group: 'affordability', format: 'currency', dollarMode: 'today' },
  { key: 'phasedSurplusTakeHomeAnnual', label: 'Phased surplus', group: 'affordability', format: 'currency', dollarMode: 'nominal' },
  { key: 'retirementGapRemaining', label: 'Retirement gap left', group: 'affordability', format: 'currency' },
  { key: 'configuredInvestMo', label: 'Configured invest/mo', group: 'affordability', format: 'currencyMo' },
  { key: 'requiredRetMo', label: 'Required retire save/mo', group: 'affordability', format: 'currencyMo' },
  { key: 'onPaceExtraMo', label: 'Goal path extra/mo', group: 'affordability', format: 'currencyMo' },

  { key: 'k401', label: 'SEP IRA balance', group: 'accounts_balances', format: 'currency' },
  { key: 'rothIra', label: 'Roth balance', group: 'accounts_balances', format: 'currency' },
  { key: 'hsa', label: 'HSA balance', group: 'accounts_balances', format: 'currency' },
  { key: 'taxable', label: 'Taxable balance', group: 'accounts_balances', format: 'currency' },
  { key: 'totalCurrent', label: 'Total configured path', group: 'accounts_balances', format: 'currency' },
  { key: 'onTrackK401', label: 'Goal path SEP', group: 'accounts_balances', format: 'currency' },
  { key: 'onTrackRothIra', label: 'Goal path Roth', group: 'accounts_balances', format: 'currency' },
  { key: 'onTrackHsa', label: 'Goal path HSA', group: 'accounts_balances', format: 'currency' },
  { key: 'onTrackTaxable', label: 'Goal path taxable', group: 'accounts_balances', format: 'currency' },
  { key: 'totalOnTrack', label: 'Total goal path', group: 'accounts_balances', format: 'currency' },

  { key: 'sepIraMonthly', label: 'SEP contrib/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'contribK401', label: 'SEP flow/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'contribRoth', label: 'Roth contrib/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'contribHsa', label: 'HSA contrib/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'contribTaxable', label: 'Taxable contrib/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'primaryCommitmentsMo', label: 'Primary commitments/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'poolAvailableMo', label: 'Pool available/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'poolAllocatedMo', label: 'Pool allocated/mo', group: 'accounts_flows', format: 'currencyMo' },
  { key: 'poolUnallocatedMo', label: 'Pool unallocated/mo', group: 'accounts_flows', format: 'currencyMo' },

  { key: 'stackBudgetToday', label: 'Stack · budget', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackBudgetNominal', label: 'Stack · budget', group: 'required_income', format: 'currency', dollarMode: 'nominal' },
  { key: 'stackDebtToday', label: 'Stack · debt', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackDebtNominal', label: 'Stack · debt', group: 'required_income', format: 'currency', dollarMode: 'nominal' },
  { key: 'stackK12Today', label: 'Stack · K-12', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackK12Nominal', label: 'Stack · K-12', group: 'required_income', format: 'currency', dollarMode: 'nominal' },
  { key: 'stackSaveToday', label: 'Stack · save', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackSaveNominal', label: 'Stack · save', group: 'required_income', format: 'currency', dollarMode: 'nominal' },
  { key: 'stackTaxToday', label: 'Stack · tax', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackTaxNominal', label: 'Stack · tax', group: 'required_income', format: 'currency', dollarMode: 'nominal' },
  { key: 'stackIncomeToday', label: 'Stack · income', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackIncomeNominal', label: 'Stack · income', group: 'required_income', format: 'currency', dollarMode: 'nominal' },
  { key: 'stackGrossRequiredToday', label: 'Stack · req gross', group: 'required_income', format: 'currency', dollarMode: 'today' },
  { key: 'stackGrossRequiredNominal', label: 'Stack · req gross', group: 'required_income', format: 'currency', dollarMode: 'nominal' },

  { key: 'checkSurplus', label: 'Δ surplus', group: 'checks', format: 'currency', defaultHidden: true },
  { key: 'checkRequiredStack', label: 'Δ req stack', group: 'checks', format: 'currency', defaultHidden: true },
  { key: 'checkPool', label: 'Δ pool', group: 'checks', format: 'currency', defaultHidden: true },
];

export function isPlanDebugCheckOk(value: number): boolean {
  return Math.abs(value) <= CHECK_TOLERANCE;
}

export function visiblePlanDebugColumns(
  dollarMode: PlanDebugDollarMode,
  enabledGroups: Set<PlanDebugColumnGroup>,
): PlanYearDebugColumn[] {
  return PLAN_DEBUG_COLUMNS.filter((col) => {
    if (!enabledGroups.has(col.group)) return false;
    if (col.dollarMode === 'today' && dollarMode === 'nominal') return false;
    if (col.dollarMode === 'nominal' && dollarMode === 'today') return false;
    return true;
  });
}

export function formatPlanDebugCell(value: string | number, format: PlanDebugColumnFormat): string {
  if (typeof value === 'string') return value || '—';
  if (format === 'text') return String(value);
  if (format === 'number') return Number.isFinite(value) ? String(Math.round(value)) : '—';
  if (format === 'pct') return `${value.toFixed(1)}%`;
  if (format === 'currencyMo') {
    if (!Number.isFinite(value)) return '—';
    return `$${Math.round(value).toLocaleString()}`;
  }
  if (!Number.isFinite(value)) return '—';
  return `$${Math.round(value).toLocaleString()}`;
}

export function exportPlanDebugCsv(
  rows: PlanYearDebugRow[],
  columns: PlanYearDebugColumn[],
): string {
  const header = ['age', ...columns.map((c) => c.label)].join(',');
  const body = rows.map((row) =>
    [
      row.age,
      ...columns.map((col) => {
        const raw = row[col.key];
        if (typeof raw === 'string') return `"${raw.replace(/"/g, '""')}"`;
        return String(raw ?? '');
      }),
    ].join(','),
  );
  return [header, ...body].join('\n');
}
